import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
} from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';

import { useGetDivisionsResourceQuery } from '../../../entities/divisions/divisionsResource/redux/divisionsResource.api';
import { useGetVehiclesResourceQuery } from '../../../entities/vehicles/vehicle/redux/vehicle.api';

interface Division {
  divisionId: number;
  name: string;
}

interface Vehicle {
  vehicleId: number;
  name: string;
}

interface AddVehiclesPopupProps {
  companyId: number;
  open: boolean;
  onClose: () => void;
  onAdd: (vehicle: Vehicle) => void;
  existingVehicleIds: number[];
}

function AddVehiclesPopup({ companyId, open, onClose, onAdd, existingVehicleIds }: AddVehiclesPopupProps) {
  const [selectedDivisionId, setSelectedDivisionId] = useState<number | null>();
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>();

  const { data: { data: divisionList } = {} } = useGetDivisionsResourceQuery(
    companyId
      ? {
          params: {
            'companyId.equals': companyId,
          },
        }
      : skipToken,
  );

  const { data: { data: vehiclesList } = {}, isFetching } = useGetVehiclesResourceQuery(
    selectedDivisionId
      ? {
          params: {
            'divisionId.equals': selectedDivisionId,
          },
        }
      : {},
  );

  const handleClose = () => {
    setSelectedDivisionId(null);
    setSelectedVehicleId(null);
    onClose();
  };

  const handleAdd = () => {
    if (!selectedVehicleId) return;

    const vehicle = vehiclesList?.find((v: Vehicle) => v.vehicleId === selectedVehicleId);
    if (!vehicle) return;

    onAdd(vehicle);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Добавить ТС</DialogTitle>
      <DialogContent sx={{ minHeight: '160px' }}>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <FormControl fullWidth>
            <InputLabel id="division-select-label">Подразделение</InputLabel>
            <Select
              labelId="division-select-label"
              value={selectedDivisionId}
              label="Подразделение"
              onChange={(e) => {
                setSelectedVehicleId(null);
                setSelectedDivisionId(Number(e.target.value));
              }}
            >
              {divisionList?.map((division: Division) => (
                <MenuItem key={division.divisionId} value={division.divisionId}>
                  {division.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {isFetching ? (
            <CircularProgress sx={{ margin: 'auto' }} />
          ) : (
            <FormControl fullWidth>
              <InputLabel id="vehicle-select-label">Транспорт</InputLabel>

              <Select
                labelId="vehicle-select-label"
                value={selectedVehicleId}
                label="Транспорт"
                onChange={(e) => setSelectedVehicleId(Number(e.target.value))}
                disabled={isFetching}
              >
                {vehiclesList?.map((vehicle: Vehicle) => (
                  <MenuItem
                    key={vehicle.vehicleId}
                    value={vehicle.vehicleId}
                    disabled={existingVehicleIds?.includes(vehicle.vehicleId)}
                  >
                    {vehicle.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: '0 24px 16px 24px' }}>
        <Button onClick={handleClose}>Отмена</Button>
        <Button onClick={handleAdd} variant="contained" color="primary" disabled={!selectedVehicleId}>
          Добавить
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddVehiclesPopup;
